/**
 * templates/registry.js - Master Section Template Registry
 * Location: /app/frontend/src/admin/page_builder/templates/
 * 
 * Central registry of all section templates available in the Page Builder.
 * Templates are code-defined (version-controlled) for safety and consistency.
 * 
 * Total: 20+ templates across 7 categories
 * - Hero (3): hero_overlay, hero_split, hero_minimal
 * - Content (7): trending_rail, featured_grid, new_releases_rail, continue_reading, genre_spotlight, community_picks, art_gallery_grid
 * - Reader (3): chapter_list, vertical_reader_stack, horizontal_reader
 * - Creator (4): creator_spotlight, creator_grid, creator_cta, leaderboard_strip
 * - CTA (4): cta_banner, transparency_footer, announcement_banner, category_spotlight
 * - Gallery (1): art_gallery_grid (shared with content)
 * - Admin (1): admin_pages_list
 */

// Import all category templates
import heroTemplates from './categories/hero';
import contentTemplates from './categories/content';
import readerTemplates from './categories/reader';
import creatorTemplates from './categories/creator';
import ctaTemplates from './categories/cta';
import adminTemplates from './categories/admin';
import seriesTemplates from './categories/series';

// Import extended metadata
import {
  getTemplateMetadata,
  getSubCategoriesForCategory,
  filterTemplatesByType,
  SubCategory,
  SubCategoryMeta,
  FilterType,
  FilterMeta,
} from './metadata';

/**
 * Template category definitions
 */
export const TemplateCategory = {
  HERO: 'hero',
  CONTENT: 'content',
  READER: 'reader',
  CREATOR: 'creator',
  CTA: 'cta',
  GALLERY: 'gallery',
  SERIES: 'series',
  ADMIN: 'admin',
};

/**
 * Category metadata
 */
export const CategoryMeta = {
  [TemplateCategory.HERO]: {
    label: 'Hero Sections',
    description: 'Full-width headers and landing sections',
    icon: 'layout',
    color: '#f97316',
  },
  [TemplateCategory.CONTENT]: {
    label: 'Content Rails & Grids',
    description: 'Display content in rails, grids, and collections',
    icon: 'grid',
    color: '#3b82f6',
  },
  [TemplateCategory.READER]: {
    label: 'Reader Presentations',
    description: 'Chapter lists and reading experiences',
    icon: 'book-open',
    color: '#22c55e',
  },
  [TemplateCategory.CREATOR]: {
    label: 'Creator Features',
    description: 'Showcase creators, rankings, and CTAs',
    icon: 'user',
    color: '#a855f7',
  },
  [TemplateCategory.CTA]: {
    label: 'CTA & Utility',
    description: 'Banners, announcements, and spotlights',
    icon: 'megaphone',
    color: '#ec4899',
  },
  [TemplateCategory.GALLERY]: {
    label: 'Gallery',
    description: 'Art portfolio and artwork displays',
    icon: 'image',
    color: '#06b6d4',
  },
  [TemplateCategory.SERIES]: {
    label: 'Series Detail',
    description: 'Components for series/novel detail pages',
    icon: 'book-open',
    color: '#eab308',
  },
  [TemplateCategory.ADMIN]: {
    label: 'Admin Sections',
    description: 'Admin-only system sections',
    icon: 'settings',
    color: '#64748b',
    adminOnly: true,
  },
};

/**
 * Master template registry - flat map of all templates by ID
 */
export const TEMPLATE_REGISTRY = {
  // Hero templates
  ...heroTemplates,
  // Content templates
  ...contentTemplates,
  // Reader templates
  ...readerTemplates,
  // Creator templates
  ...creatorTemplates,
  // CTA templates
  ...ctaTemplates,
  // Series detail templates
  ...seriesTemplates,
  // Admin templates (for recursive self-editing)
  ...adminTemplates,
};

/**
 * Templates grouped by category
 */
export const TEMPLATES_BY_CATEGORY = {
  [TemplateCategory.HERO]: Object.values(heroTemplates),
  [TemplateCategory.CONTENT]: Object.values(contentTemplates).filter(t => t.category !== 'gallery'),
  [TemplateCategory.READER]: Object.values(readerTemplates),
  [TemplateCategory.CREATOR]: Object.values(creatorTemplates),
  [TemplateCategory.CTA]: Object.values(ctaTemplates),
  [TemplateCategory.GALLERY]: Object.values(contentTemplates).filter(t => t.category === 'gallery'),
  [TemplateCategory.SERIES]: Object.values(seriesTemplates),
  [TemplateCategory.ADMIN]: Object.values(adminTemplates),
};

/**
 * Get all templates as flat array
 * @returns {Array} All templates
 */
export function getAllTemplates() {
  return Object.values(TEMPLATE_REGISTRY);
}

/**
 * Get template by ID
 * @param {string} templateId - Template ID
 * @returns {Object|null} Template definition or null
 */
export function getTemplateById(templateId) {
  return TEMPLATE_REGISTRY[templateId] || null;
}

/**
 * Get templates by category
 * @param {string} category - Category key
 * @returns {Array} Templates in category
 */
export function getTemplatesByCategory(category) {
  return TEMPLATES_BY_CATEGORY[category] || [];
}

/**
 * Get all categories with their templates
 * @returns {Array<{category: string, meta: Object, templates: Array}>}
 */
export function getCategoriesWithTemplates() {
  return Object.values(TemplateCategory).map(category => ({
    category,
    meta: CategoryMeta[category],
    templates: TEMPLATES_BY_CATEGORY[category] || [],
  }));
}

/**
 * Search templates by name, description, or tags
 * @param {string} query - Search query
 * @returns {Array} Matching templates with merged metadata
 */
export function searchTemplates(query) {
  const allTemplates = getAllTemplates();
  if (!query) return allTemplates;
  
  const lowerQuery = query.toLowerCase();
  return allTemplates.filter(template => {
    const meta = getTemplateMetadata(template.id);
    return (
      template.name.toLowerCase().includes(lowerQuery) ||
      template.description.toLowerCase().includes(lowerQuery) ||
      template.category.toLowerCase().includes(lowerQuery) ||
      (meta.tags && meta.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
    );
  });
}

/**
 * Get templates with merged metadata
 * @returns {Array} All templates with extended metadata merged in
 */
export function getTemplatesWithMetadata() {
  return getAllTemplates().map(template => ({
    ...template,
    ...getTemplateMetadata(template.id),
  }));
}

/**
 * Get templates by sub-category
 * @param {string} subCategory - Sub-category ID
 * @returns {Array} Templates in sub-category with metadata
 */
export function getTemplatesBySubCategory(subCategory) {
  return getTemplatesWithMetadata().filter(t => t.subCategory === subCategory);
}

/**
 * Get templates filtered by filter type
 * @param {string} filterType - Filter type from FilterType enum
 * @returns {Array} Filtered templates with metadata
 */
export function getFilteredTemplates(filterType) {
  return filterTemplatesByType(getTemplatesWithMetadata(), filterType);
}

/**
 * Create a block config from a template
 * This is used when inserting a template into a page.
 * 
 * @param {string} templateId - Template ID
 * @param {string} [blockId] - Optional custom block ID
 * @returns {Object|null} Block configuration ready for insertion
 */
export function createBlockFromTemplate(templateId, blockId = null) {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  // Generate unique block ID if not provided
  const id = blockId || `${templateId}_${Date.now()}`;
  
  return {
    id,
    type: template.blockType,
    enabled: true,
    order: 0, // Caller should set this
    props: { ...template.defaultProps },
    layout: template.layout ? { ...template.layout } : null,
    background: template.background ? { ...template.background } : null,
    // Template reference for later customization
    _templateId: templateId,
    _templateVersion: '1.0',
  };
}

/**
 * Get template count by category
 * @returns {Object} Map of category -> count
 */
export function getTemplateCounts() {
  const counts = {};
  for (const category of Object.values(TemplateCategory)) {
    counts[category] = (TEMPLATES_BY_CATEGORY[category] || []).length;
  }
  counts.total = getAllTemplates().length;
  return counts;
}

export default {
  TemplateCategory,
  CategoryMeta,
  TEMPLATE_REGISTRY,
  TEMPLATES_BY_CATEGORY,
  getAllTemplates,
  getTemplateById,
  getTemplatesByCategory,
  getCategoriesWithTemplates,
  searchTemplates,
  createBlockFromTemplate,
  getTemplateCounts,
  // Extended metadata exports
  getTemplatesWithMetadata,
  getTemplatesBySubCategory,
  getFilteredTemplates,
  getTemplateMetadata,
  getSubCategoriesForCategory,
  filterTemplatesByType,
  SubCategory,
  SubCategoryMeta,
  FilterType,
  FilterMeta,
};

// Re-export metadata functions and enums
export {
  getTemplateMetadata,
  getSubCategoriesForCategory,
  filterTemplatesByType,
  SubCategory,
  SubCategoryMeta,
  FilterType,
  FilterMeta,
} from './metadata';
